import { useEffect, useState } from "react";
import { ArrowLeft, Calendar, MapPin, Plus, Settings, Trash2, Trophy, User } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

export default function GestioneCompetizioni() {
  const [competitions, setCompetitions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", date: "", responsabile_ids: [], type: "singolo" });

  const load = async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([api.get("/competitions"), api.get("/users")]);
      setCompetitions(c.data);
      setUsers(u.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleResp = (id) => {
    setForm((f) => ({
      ...f,
      responsabile_ids: f.responsabile_ids.includes(id)
        ? f.responsabile_ids.filter((x) => x !== id)
        : [...f.responsabile_ids, id],
    }));
  };

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.responsabile_ids || form.responsabile_ids.length === 0) {
      toast.error("Seleziona almeno un responsabile");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/competitions", form);
      toast.success("Competizione creata");
      setOpen(false);
      setForm({ name: "", location: "", date: "", responsabile_ids: [], type: "singolo" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/competitions/${id}`);
      toast.success("Competizione eliminata");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Layout>
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-admin">
        <ArrowLeft className="h-4 w-4" /> Torna al pannello
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Amministrazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-2">Gestione Competizioni</h1>
          <p className="text-slate-500 mt-2">Crea le competizioni e assegnale a un responsabile.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-blue-700 hover:bg-blue-800 text-white h-11 px-5"
              disabled={users.length === 0}
              data-testid="open-create-competition"
            >
              <Plus className="h-4 w-4 mr-2" /> Nuova Competizione
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-600" /> Nuova competizione</DialogTitle>
              <DialogDescription>Compila i dati dell'evento.</DialogDescription>
            </DialogHeader>
            <form onSubmit={onCreate} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Nome evento *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11" placeholder="Trofeo CIBS 2026" data-testid="new-comp-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Luogo *</Label>
                <Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-2 h-11" placeholder="Palazzetto, Roma" data-testid="new-comp-location" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Data *</Label>
                <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 h-11" data-testid="new-comp-date" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Responsabili * <span className="font-normal text-slate-500">(uno o più)</span></Label>
                <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 divide-y divide-slate-200" data-testid="new-comp-responsabili">
                  {users.length === 0 ? (
                    <p className="text-sm text-slate-500 p-3">Nessun utente disponibile.</p>
                  ) : (
                    users.map((u) => {
                      const checked = form.responsabile_ids.includes(u.id);
                      return (
                        <label
                          key={u.id}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition ${checked ? "bg-blue-50" : "hover:bg-white"}`}
                          data-testid={`responsabile-option-${u.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleResp(u.id)}
                            className="h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-200"
                          />
                          <span className="text-sm">
                            <span className="font-medium text-slate-900">{u.name}</span>
                            <span className="text-slate-500"> — {u.email}</span>
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                {form.responsabile_ids.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1.5">{form.responsabile_ids.length} responsabil{form.responsabile_ids.length === 1 ? "e" : "i"} selezionat{form.responsabile_ids.length === 1 ? "o" : "i"}</p>
                )}
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Tipologia *</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { v: "singolo", label: "Dance Analysis", sub: "Ballo Singolo" },
                    { v: "latin", label: "Dance Analysis", sub: "Latin Style" },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => setForm({ ...form, type: opt.v })}
                      className={`text-left rounded-xl border-2 px-4 py-3 transition ${form.type === opt.v ? "border-blue-700 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`new-comp-type-${opt.v}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">{opt.label}</div>
                      <div className="font-display font-semibold text-slate-900 mt-0.5">{opt.sub}</div>
                    </button>
                  ))}
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800" data-testid="submit-create-competition">
                  {submitting ? "Creazione…" : "Crea"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {users.length === 0 && !loading && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 mb-6 text-sm">
          Nessun utente disponibile. Crea prima almeno un utente in <Link to="/admin/utenti" className="underline font-semibold">Gestione Utenti</Link>.
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-slate-500 text-sm">Caricamento…</div>
      ) : competitions.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-10 text-center text-slate-500 text-sm">
          Nessuna competizione creata.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {competitions.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl ring-1 ring-slate-200 p-6 hover:shadow-md transition flex flex-col gap-3" data-testid={`comp-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
                  <Trophy className="h-5 w-5" />
                </div>
                {c.sent ? (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Inviata</Badge>
                ) : (
                  <Badge variant="outline" className="border-slate-300 text-slate-600">Bozza</Badge>
                )}
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900">{c.name}</h3>
              <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700 -mt-1">
                {c.type === "latin" ? "Dance Analysis · Latin Style" : "Dance Analysis · Ballo Singolo"}
              </div>
              <div className="flex items-center text-sm text-slate-600 gap-2">
                <MapPin className="h-4 w-4 text-slate-400" /> {c.location}
              </div>
              <div className="flex items-center text-sm text-slate-600 gap-2">
                <Calendar className="h-4 w-4 text-slate-400" /> {new Date(c.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}
              </div>
              <div className="flex items-start text-sm text-slate-600 gap-2">
                <User className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <span className="break-words">{(c.responsabile_names && c.responsabile_names.length > 0) ? c.responsabile_names.join(" · ") : c.responsabile_name}</span>
              </div>
              <div className="text-xs text-slate-400 flex items-center justify-between mt-1">
                <span>{c.athletes?.length || 0} atleti · {c.judges?.length || 0} giudici</span>
                <div className="flex items-center gap-1">
                  <Link
                    to={`/responsabile/competizioni/${c.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline px-2 py-1"
                    data-testid={`manage-comp-${c.id}`}
                  >
                    <Settings className="h-3.5 w-3.5" /> Gestisci
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 -mr-2" data-testid={`delete-comp-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare "{c.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>L'azione non può essere annullata.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => onDelete(c.id)} data-testid={`confirm-delete-comp-${c.id}`}>
                          Elimina
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
